import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack } from "@mui/material";
import { AddCardOutlined, ReceiptLongOutlined, SellOutlined } from "@mui/icons-material";
import Swal from "sweetalert2";
import { getMyBranchCash } from "api/branchCash";
import BranchCashDepositDialog from "components/BranchCashDepositDialog";
import BranchExpenseDialog from "components/BranchExpenseDialog";
import ServiceChargeDialog from "components/ServiceChargeDialog";
import DailySummary from "scenes/financial/DailySummary";
import StationSwitcher from "components/StationSwitcher";
import { useSetBreadcrumb } from "hooks/BreadcrumbContext";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();

export default function BranchCashStation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stationId } = useParams();
  const [balances, setBalances] = useState([]);
  const [rate, setRate] = useState(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [serviceChargeOpen, setServiceChargeOpen] = useState(false);
  // Bumped on every recorded movement so DailySummary (a sibling, not a child
  // of this page's own `load`) refetches its feed too — otherwise a just-recorded
  // expense/service charge/deposit only updates the balances state here.
  const [refreshToken, setRefreshToken] = useState(0);

  // Balances only — the ledger/transaction list itself lives in DailySummary below
  // (searchable by date range + customer), so a single small page is all we need.
  const load = useCallback(async () => {
    const res = await getMyBranchCash({ stationId, limit: 1 });
    if (res && res.success) {
      setBalances(res.balances || []);
      setRate(res.rate || null);
    }
  }, [stationId]);
  useEffect(() => { load(); }, [load]);

  const station = balances.find((b) => b.stationId === stationId) || null;

  useSetBreadcrumb([
    { label: t("branchCash.title"), to: "/branch-cash" },
    { label: station?.stationName || stationId },
  ]);

  const onDepositSuccess = () => {
    setDepositOpen(false);
    load();
    setRefreshToken((n) => n + 1);
    Swal.fire({ icon: "success", text: t("branchCash.depositRecorded"), toast: true, position: "top-right", timer: 2200, showConfirmButton: false });
  };

  const onExpenseSuccess = (res) => {
    setExpenseOpen(false);
    load();
    setRefreshToken((n) => n + 1);
    Swal.fire({
      icon: "success",
      text: t(res.status === "pending_approval" ? "branchCash.expenseSubmitted" : "branchCash.expenseRecorded"),
      toast: true, position: "top-right", timer: 2200, showConfirmButton: false,
    });
  };

  const onServiceChargeSuccess = (res) => {
    setServiceChargeOpen(false);
    load();
    setRefreshToken((n) => n + 1);
    Swal.fire({
      icon: "success",
      text: t(res.status === "pending_approval" ? "branchCash.serviceChargeSubmitted" : "branchCash.serviceChargeRecorded"),
      toast: true, position: "top-right", timer: 2200, showConfirmButton: false,
    });
  };

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1100, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "14px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{initials(station?.stationName || stationId)}</Box>
          {balances.length > 1 ? (
            <StationSwitcher
              size="lg"
              value={stationId}
              options={balances.map((b) => ({ id: b.stationId, name: b.stationName }))}
              onChange={(id) => navigate(`/branch-cash/${encodeURIComponent(id)}`)}
            />
          ) : (
            <Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{station?.stationName || stationId}</Box>
          )}
        </Box>
        <Stack direction="row" spacing={1.25}>
          <Button variant="outlined" color="secondary" startIcon={<SellOutlined />} disabled={!station} onClick={() => setServiceChargeOpen(true)} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
            {t("branchCash.recordServiceCharge")}
          </Button>
          <Button variant="outlined" color="secondary" startIcon={<ReceiptLongOutlined />} disabled={!station} onClick={() => setExpenseOpen(true)} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
            {t("branchCash.recordExpense")}
          </Button>
          <Button variant="contained" color="secondary" startIcon={<AddCardOutlined />} disabled={!station} onClick={() => setDepositOpen(true)} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
            {t("branchCash.depositToBank")}
          </Button>
        </Stack>
      </Box>

      {!station ? (
        <Box sx={{ mt: "22px", background: "#fff", border: "1px solid #ece9f1", borderRadius: "20px", p: "40px", textAlign: "center", color: "#a8a3b4" }}>{t("branchCash.noStations")}</Box>
      ) : (
        <>
          {/* customer visits + cash movements, filterable by date range/customer —
              DailySummary's own "TODAY" KPI row already covers this station's
              current balances, so no separate top-level KPI strip is duplicated
              here (see also scenes/financial/transactions.jsx's isBranch branch). */}
          <Box sx={{ mt: "22px" }}>
            <DailySummary stationId={stationId} mode="branch" refreshToken={refreshToken} />
          </Box>
        </>
      )}

      <BranchCashDepositDialog open={depositOpen} stations={station ? [station] : []} onClose={() => setDepositOpen(false)} onSuccess={onDepositSuccess} />
      <BranchExpenseDialog open={expenseOpen} station={station ? { id: station.stationId, name: station.stationName } : null} onClose={() => setExpenseOpen(false)} onSuccess={onExpenseSuccess} />
      <ServiceChargeDialog open={serviceChargeOpen} station={station ? { id: station.stationId, name: station.stationName } : null} rate={rate} onClose={() => setServiceChargeOpen(false)} onSuccess={onServiceChargeSuccess} />
    </Box>
  );
}
